import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ClipboardCheck, Clock, Trophy, ArrowRight, Target, TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { PageContainer } from '../../components/shared/PageContainer';
import { PageHero, HeroStatPill } from '../../components/shared/PageHero';
import { WorldZone } from '../../components/shared/WorldZone';
import { itemVariants } from '../../components/shared/animations';
import { useDjaySelection } from '../../mascot';
import { GroupId } from '../../config/systemConfig';
import { error as logError } from '../../utils/logger';
import { AssessmentConfig, AssessmentDomainId, AssessmentHistoryEntry, AssessmentReport, SubTestDefinition } from '../types';
import { getAssessmentHistory, checkDomainCooldown, getReport } from '../utils/assessmentStorage';
import { getAvailableSubTests, DOMAIN_META, DOMAIN_MODULE_MAP } from '../utils/assessmentDomainConfig';
import { dataLayer } from '../../data/dataLayer';
interface CooldownState {
  canStart: boolean;
  remainingMs: number;
  lastCompletedAt: number;
}
interface AssessmentPageProps {
  selectedGroup: GroupId;
  onStart: (config: AssessmentConfig) => void;
  onViewReport: (report: AssessmentReport) => void;
  learnerId?: string;
}
export function AssessmentPage({
  selectedGroup,
  onStart,
  onViewReport,
  learnerId
}: AssessmentPageProps) {
  const {
    selectedVariant
  } = useDjaySelection();
  const [history, setHistory] = useState<AssessmentHistoryEntry[]>([]);
  const [reports, setReports] = useState<Record<string, AssessmentReport>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [domainCooldowns, setDomainCooldowns] = useState<Record<AssessmentDomainId, CooldownState>>({
    rekenen: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    taalverzorging: {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    },
    'begrijpend-lezen': {
      canStart: true,
      remainingMs: 0,
      lastCompletedAt: 0
    }
  });
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      setIsLoading(true);
      try {
        const h = await getAssessmentHistory(selectedGroup);
        if (isMounted) setHistory(h);
        // Load reports for the history entries
        const loadedReports: Record<string, AssessmentReport> = {};
        await Promise.all(h.map(async entry => {
          const report = await getReport(entry.attemptId);
          if (report) {
            loadedReports[entry.attemptId] = report;
          }
        }));
        if (isMounted) setReports(loadedReports);
        // Load teacher assignments
        if (learnerId) {
          const assignmentsData = await dataLayer.getTeacherAssignments();
          const allAssignments = Object.values(assignmentsData);
          const myAssignments = allAssignments.filter((a: any) => {
            if (a.status !== 'active') return false;
            if (a.deadline && Date.now() > a.deadline) return false;
            // Skip assignments already completed by this student
            const completedBy = a.completedByStudentIds as string[] || [];
            if (completedBy.includes(learnerId!)) return false;
            if (a.wholeClass && a.group === selectedGroup) return true;
            if (a.targetStudentIds && a.targetStudentIds.includes(learnerId)) return true;
            return false;
          });
          if (isMounted) setAssignments(myAssignments.sort((a: any, b: any) => b.createdAt - a.createdAt));
        }
      } catch (e) {
        logError('Failed to load assessment history', e);
      }
      try {
        const domains: AssessmentDomainId[] = ['rekenen', 'taalverzorging', 'begrijpend-lezen'];
        const cooldowns: Record<string, CooldownState> = {};
        await Promise.all(domains.map(async domainId => {
          cooldowns[domainId] = await checkDomainCooldown(selectedGroup, domainId);
        }));
        if (isMounted) setDomainCooldowns(cooldowns as Record<AssessmentDomainId, CooldownState>);
      } catch (e) {
        logError('Failed to check cooldowns', e);
      }
      if (isMounted) setIsLoading(false);
    };
    loadData();
    return () => {
      isMounted = false;
    };
  }, [selectedGroup, learnerId]);
  const handleStartSubTest = (subTest: SubTestDefinition) => {
    const config: AssessmentConfig = {
      questionCount: subTest.questionCount,
      moduleFilter: subTest.modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now(),
      domainId: subTest.domainId
    };
    onStart(config);
  };
  const handleStartAssignment = (assignment: any) => {
    const modules = DOMAIN_MODULE_MAP[assignment.domainId as keyof typeof DOMAIN_MODULE_MAP] || null;
    const config: AssessmentConfig = {
      domainId: assignment.domainId,
      mode: assignment.type === 'targeted' ? 'targeted' : 'general',
      targetFamilyIds: assignment.selectedFamilyIds || [],
      assignmentId: assignment.id,
      questionCount: assignment.questionCount || 30,
      moduleFilter: modules,
      minModules: 1,
      countsForMastery: false,
      seed: Date.now()
    };
    onStart(config);
  };
  const hasHistory = history.length > 0;
  const bestScore = hasHistory ? Math.max(...history.map(h => h.score)) : null;
  const availableSubTests = getAvailableSubTests(selectedGroup);
  // Check if ANY domain is on cooldown (for the global cooldown banner)
  const anyDomainOnCooldown = Object.values(domainCooldowns).some(c => !c.canStart);
  const maxRemainingMs = Math.max(...Object.values(domainCooldowns).map(c => c.remainingMs));
  return <PageContainer data-id="element-819">
      <motion.div variants={itemVariants} data-id="element-820">
        <PageHero title="Toets" subtitle={hasHistory ? 'Klaar om te laten zien hoeveel je gegroeid bent in deze deeltoetsen?' : 'Klaar voor je eerste deeltoets? Kies een vak en laat zien wat je al kunt!'} gradientClasses="from-orange-500 via-amber-500 to-yellow-500" djayEmotion={hasHistory ? 'excited' : 'encouraging'} djayVariant={selectedVariant} icon={ClipboardCheck} iconBgClass="bg-orange-400" floatingEmojis={['📝', '⭐']} data-id="element-821">
          <HeroStatPill icon={Target} data-id="element-822">Groep {selectedGroup}</HeroStatPill>
          {hasHistory && <HeroStatPill icon={ClipboardCheck} data-id="element-823">
              {history.length} toets{history.length !== 1 ? 'en' : ''} gemaakt
            </HeroStatPill>}
          {bestScore !== null && <HeroStatPill icon={Trophy} data-id="element-824">Topscore: {bestScore}%</HeroStatPill>}
        </PageHero>
      </motion.div>

      {assignments.length > 0 && <motion.div variants={itemVariants} data-id="element-825">
          <WorldZone title="Klaargezette Toetsen" icon={Sparkles} accentColor="bg-indigo-100 text-indigo-600" djayMessage="Je juf/meester heeft een toets voor je klaargezet!" djayEmotion="excited" djayVariant={selectedVariant} data-id="element-826">
            <div className="space-y-4" data-id="element-827">
              {assignments.map(assignment => {
            const meta = DOMAIN_META[assignment.domainId as keyof typeof DOMAIN_META];
            const Icon = meta?.icon;
            return <Card key={assignment.id} className="p-5 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-blue-50/30" data-id="element-828">
                    <div className="flex items-start gap-4" data-id="element-829">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${meta?.bgColor || 'bg-slate-100'} ${meta?.color || 'text-slate-500'}`} data-id="element-830">
                        {Icon ? <Icon className="w-6 h-6" data-id="element-831" /> : '📝'}
                      </div>
                      <div className="flex-1" data-id="element-832">
                        <h3 className="font-bold text-slate-900 mb-1" data-id="element-833">
                          {assignment.domainLabel}
                        </h3>
                        {assignment.type === 'targeted' && assignment.selectedSkillSummary && <p className="text-sm text-slate-600 mb-3" data-id="element-834">
                              {assignment.selectedSkillSummary}
                            </p>}
                        <div className="flex items-center gap-4 text-sm text-slate-500 mb-4" data-id="element-835">
                          <div className="flex items-center gap-1.5" data-id="element-836">
                            <Clock className="w-4 h-4" data-id="element-837" />
                            ~15 min
                          </div>
                          {assignment.deadline && <div className="flex items-center gap-1.5 text-orange-600" data-id="element-838">
                              <span data-id="element-839">Deadline:</span>
                              {new Date(assignment.deadline).toLocaleDateString('nl-NL')}
                            </div>}
                        </div>
                        <Button onClick={() => handleStartAssignment(assignment)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-id="element-840">
                          Start Toets <ArrowRight className="w-4 h-4 ml-2" data-id="element-841" />
                        </Button>
                      </div>
                    </div>
                  </Card>;
          })}
            </div>
          </WorldZone>
        </motion.div>}

      <motion.div variants={itemVariants} data-id="element-842">
        <WorldZone title="Start een toets" icon={Target} accentColor="bg-orange-100 text-orange-600" djayMessage={hasHistory ? 'Laat zien dat je gegroeid bent! Je kunt het!' : 'Doe rustig je best. Fouten maken mag!'} djayEmotion="encouraging" djayVariant={selectedVariant} data-id="element-843">
          <div className="space-y-6" data-id="element-844">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-845">
              {availableSubTests.map((subTest, i) => {
              const meta = DOMAIN_META[subTest.domainId];
              const cooldown = domainCooldowns[subTest.domainId];
              const canStartThis = cooldown?.canStart !== false && !isLoading;
              return <motion.div key={subTest.domainId} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: i * 0.1
              }} className="h-full" data-id="element-846">
                    <button onClick={() => handleStartSubTest(subTest)} disabled={!canStartThis} className={`w-full text-left bg-white rounded-2xl p-4 border-2 shadow-sm transition-all flex flex-col h-full ${!canStartThis ? 'opacity-60 cursor-not-allowed border-slate-200' : `hover:shadow-md hover:-translate-y-1 ${meta.borderColor}`}`} data-id="element-847">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${meta.bgColor} ${meta.color}`} data-id="element-848">
                        <meta.icon className="w-5 h-5" data-id="element-849" />
                      </div>
                      <h3 className={`font-bold text-base mb-1 ${meta.color}`} data-id="element-850">
                        {subTest.label}
                      </h3>
                      <p className="text-xs text-slate-600 font-medium mb-4 flex-grow" data-id="element-851">
                        {meta.description}
                      </p>

                      <div className="space-y-2 mt-auto" data-id="element-852">
                        <div className="flex items-center text-xs text-slate-500 font-medium" data-id="element-853">
                          <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" data-id="element-854" />
                          {subTest.questionCount} vragen
                        </div>
                        <div className="flex items-center text-xs text-slate-500 font-medium" data-id="element-855">
                          <Clock className="w-3.5 h-3.5 mr-1.5" data-id="element-856" />~
                          {subTest.estimatedMinutes} minuten
                        </div>

                        {subTest.shortNote && <div className={`mt-2 text-[10px] font-bold px-2 py-1 rounded-md inline-block ${meta.bgColor} ${meta.color}`} data-id="element-857">
                            {subTest.shortNote}
                          </div>}

                        {cooldown && !cooldown.canStart && <div className="mt-2 text-[10px] font-bold px-2 py-1 rounded-md inline-block bg-slate-100 text-slate-500" data-id="element-858">
                            ⏳ Nog {formatRemainingTime(cooldown.remainingMs)}
                          </div>}
                      </div>
                    </button>
                  </motion.div>;
            })}
            </div>

            <div className="bg-amber-50 border-2 border-amber-100 rounded-2xl p-5 relative overflow-hidden" data-id="element-859">
              <div className="absolute top-0 right-0 w-32 h-32 bg-amber-100/50 rounded-full -mr-10 -mt-10 blur-2xl" data-id="element-860" />
              <div className="flex items-start gap-4 relative z-10" data-id="element-861">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm text-2xl flex-shrink-0 border border-amber-100" data-id="element-862">
                  💡
                </div>
                <div data-id="element-863">
                  <h3 className="font-bold text-amber-900 text-lg mb-1" data-id="element-864">Tip</h3>
                  <p className="text-amber-800 font-medium leading-relaxed" data-id="element-865">
                    Kies een vakgebied om te toetsen. Deze deeltoetsen laten
                    zien waar je staat en tellen niet mee voor je
                    oefen-voortgang. Lees elke vraag goed!
                  </p>
                </div>
              </div>
            </div>

            {anyDomainOnCooldown && <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-5 relative overflow-hidden" data-id="element-866">
                <div className="flex items-start gap-4" data-id="element-867">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm text-2xl flex-shrink-0 border border-slate-200" data-id="element-868">
                    ⏳
                  </div>
                  <div data-id="element-869">
                    <h3 className="font-bold text-slate-700 text-lg mb-1" data-id="element-870">
                      Even wachten
                    </h3>
                    <p className="text-slate-600 font-medium leading-relaxed" data-id="element-871">
                      Je hebt recent een toets gemaakt. Sommige deeltoetsen zijn
                      nog niet beschikbaar. Wacht nog{' '}
                      <strong data-id="element-872">{formatRemainingTime(maxRemainingMs)}</strong>{' '}
                      voor alle toetsen.
                    </p>
                  </div>
                </div>
              </div>}
          </div>
        </WorldZone>
      </motion.div>

      <motion.div variants={itemVariants} data-id="element-873">
        <WorldZone title="Jouw resultaten" icon={Trophy} accentColor="bg-amber-100 text-amber-600" isLast={true} data-id="element-874">
          {isLoading ? <div className="p-8 text-center text-slate-500" data-id="element-875">Laden...</div> : hasHistory ? <div className="space-y-4" data-id="element-876">
              {history.map((entry, index) => {
            const date = new Date(entry.completedAt);
            const dateStr = date.toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            });
            const scoreColor = entry.score >= 70 ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : entry.score >= 50 ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-rose-700 bg-rose-50 border-rose-200';
            const report = reports[entry.attemptId];
            // Calculate trend compared to previous assessment of the SAME domain
            const prevEntry = history.slice(index + 1).find(h => h.domainId === entry.domainId);
            let TrendIcon = Minus;
            let trendColor = 'text-slate-400';
            if (prevEntry) {
              if (entry.score > prevEntry.score) {
                TrendIcon = TrendingUp;
                trendColor = 'text-emerald-500';
              } else if (entry.score < prevEntry.score) {
                TrendIcon = TrendingDown;
                trendColor = 'text-rose-500';
              }
            }
            const domainMeta = entry.domainId ? DOMAIN_META[entry.domainId] : null;
            return <Card key={entry.attemptId} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-877">
                    <div className="flex items-center gap-4" data-id="element-878">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${scoreColor}`} data-id="element-879">
                        <span className="font-black text-lg" data-id="element-880">
                          {entry.score}%
                        </span>
                      </div>
                      <div data-id="element-881">
                        <div className="flex items-center gap-2" data-id="element-882">
                          <h3 className="font-bold text-slate-900" data-id="element-883">
                            {domainMeta ? domainMeta.shortLabel : 'Toets'}
                          </h3>
                          {domainMeta && <span className={`flex items-center justify-center w-6 h-6 rounded ${domainMeta.bgColor} ${domainMeta.color}`} data-id="element-884">
                              <domainMeta.icon className="w-3.5 h-3.5" data-id="element-885" />
                            </span>}
                          {prevEntry && <TrendIcon className={`w-4 h-4 ${trendColor}`} data-id="element-886" />}
                        </div>
                        <p className="text-sm text-slate-500 font-medium flex items-center gap-2" data-id="element-887">
                          <span className="flex items-center gap-1" data-id="element-888">
                            <ClipboardCheck className="w-3.5 h-3.5" data-id="element-889" />
                            {entry.totalQuestions} vragen
                          </span>
                          <span className="text-slate-300" data-id="element-890">·</span>
                          <span data-id="element-891">{dateStr}</span>
                        </p>
                      </div>
                    </div>
                    {report && <Button onClick={() => onViewReport(report)} variant="secondary" className="bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200 w-full sm:w-auto" data-id="element-892">
                        Bekijk Rapport <ArrowRight className="w-4 h-4 ml-2" data-id="element-893" />
                      </Button>}
                  </Card>;
          })}
              <p className="text-xs text-slate-400 text-center mt-4 font-medium" data-id="element-894">
                Indicatieve niveaubepaling — geen officiële Cito-toets
              </p>
            </div> : <div className="bg-white border border-amber-100 rounded-2xl p-8 text-center" data-id="element-895">
              <div className="text-4xl mb-3" data-id="element-896">🌟</div>
              <p className="text-slate-900 font-bold text-lg" data-id="element-897">
                Na je eerste toets zie je hier je resultaten!
              </p>
              <p className="text-slate-500 font-medium mt-1" data-id="element-898">
                Start hierboven een toets om te zien waar je staat.
              </p>
            </div>}
        </WorldZone>
      </motion.div>
    </PageContainer>;
}
function formatRemainingTime(ms: number): string {
  const minutes = Math.ceil(ms / (60 * 1000));
  if (minutes <= 1) return 'minder dan een minuut';
  if (minutes < 60) return `${minutes} minuten`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours} uur`;
  return `${hours} uur en ${remainingMinutes} minuten`;
}